/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import DataGridButton from "../../components/DataGridButton";
import { Box, Grid, Typography, useTheme } from "@mui/material";
import { Sync } from "@mui/icons-material";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import { useListBots, useDeleteUser } from "../../api/users";
import { dataGridButtonTypography } from "../../styles/DataGridStyles";
import {
  dataGridButtonStyles,
  iamDividerStyles,
} from "../../styles/buttonStyles";
import { tabbedDataGridPage } from "../../styles/commonPageStyles";
import { adaptUsers } from "../../adapters/users";
import TimeCell from "../../components/TimeCell";
import DataTable from "../../components/DataTable";
import useIamModal from "../IamModal/UseIamModal";
import { useCreateBotDialog } from "./dialog/DialogHooks";
import { useHasPermission } from "../../api/iam";

// Column layout so the grid takes every pixel left under the header row,
// matching the control planes / clusters pages.
const styles = {
  mainBox: {
    ...tabbedDataGridPage.tabContentMainBox,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  headerGrid: { ...tabbedDataGridPage.gridContainer, flexShrink: 0 },
  dataTable: { flex: 1, minHeight: 0 },
};

const Bots = ({ displayNotification, setConfirmationDialog }) => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);

  const { IamModal, IamButton, canViewIamModal } = useIamModal(
    "/user-management/users"
  );

  const [bots, setBots] = useState([]);

  const {
    postData: listBots,
    response: listBotsResp,
    isLoading: isLoadingPrs,
  } = useListBots();

  const {
    postData: deleteUser,
    response: deleteUserResp,
    responseStatus: deleteUserStatus,
  } = useDeleteUser();

  const { createBotDialog, setOpenCreateBotDialog } = useCreateBotDialog(
    listBots,
    displayNotification
  );

  useEffect(() => {
    listBots();
  }, []);

  useEffect(() => {
    if (Array.isArray(listBotsResp?.users)) {
      setBots(adaptUsers(listBotsResp.users));
    }
  }, [listBotsResp]);

  useEffect(() => {
    displayNotification(deleteUserResp, deleteUserStatus, () => {
      listBots();
    });
  }, [deleteUserResp, deleteUserStatus]);

  const handleDelete = (user) => {
    setConfirmationDialog({
      isOpen: true,
      title: "Delete User",
      content: `You are going to delete the user: "${user?.emailId}"`,
      handleDialogConfirm: () => {
        deleteUser(`/${user?.id}`);
      },
    });
  };

  const columns = [
    {
      field: "name",
      renderHeader: () => "Name",
      flex: 1.5,
      cellClassName: "name-column--cell",
      renderCell: ({ row: { name } }) => name || "",
    },
    {
      field: "description",
      flex: 3,
      renderHeader: () => "Description",
      renderCell: ({ row: { description } }) => description ?? "",
    },
    {
      field: "emailId",
      flex: 2,
      renderHeader: () => "Email",
      renderCell: ({ row: { emailId } }) => emailId ?? "",
    },
    {
      field: "createdBy",
      flex: 2,
      renderHeader: () => "Created By",
      renderCell: ({ row: { createdBy } }) => createdBy ?? "",
    },
    {
      field: "createdOn",
      flex: 1.3,
      minWidth: 118,
      renderHeader: () => "Created On",
      renderCell: ({ row: { createdOn } }) => <TimeCell value={createdOn} />,
    },
    {
      field: "actions",
      type: "actions",
      width: 30,
      getActions: ({ row }) =>
        [
          <GridActionsCellItem
            label="Delete"
            onClick={() => handleDelete(row)}
            showInMenu
          />,
        ].filter(Boolean),
    },
  ];

  return (
    <Box sx={styles.mainBox}>
      {IamModal}
      {createBotDialog}
      <Grid sx={styles.headerGrid} container columns={2}>
        <Grid item xs={1}>
          <Header title="Bots" subtitle="list of convoy bots" />
        </Grid>
        <Grid item xs={1}>
          <Box sx={tabbedDataGridPage.topButtonStack}>
            {IamButton}
            {canViewIamModal ? <Box sx={iamDividerStyles(colors)} /> : null}
            <DataGridButton
              sx={dataGridButtonStyles.pageTopButtons}
              onClick={() => listBots()}
            >
              <Sync sx={{ fontSize: 16, color: "#fff" }} />
              <Typography sx={dataGridButtonTypography(colors, 500)}>
                RELOAD
              </Typography>
            </DataGridButton>
            {useHasPermission(
              "/user-management/users",
              "POST",
              "CREATE_BOT"
            ) ? (
              <DataGridButton
                sx={dataGridButtonStyles.pageTopButtons}
                onClick={setOpenCreateBotDialog}
              >
                <Typography sx={dataGridButtonTypography(colors, 500)}>
                  CREATE
                </Typography>
              </DataGridButton>
            ) : (
              <></>
            )}
          </Box>
        </Grid>
      </Grid>
      <DataTable
        hideFooter
        height="100%"
        sx={styles.dataTable}
        getRowId={(row) => row?.id}
        loading={isLoadingPrs}
        rows={bots}
        columns={columns}
      />
    </Box>
  );
};

export default Bots;
